import type { ScheduledJob } from "lib/fleetMissionStorage/FleetScheduleClient";

type WallTime = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
};

const DAY_MS = 86_400_000;
const formatters = new Map<string, Intl.DateTimeFormat>();

const formatterFor = (timezone: string) => {
    let formatter = formatters.get(timezone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: timezone,
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        formatters.set(timezone, formatter);
    }
    return formatter;
};

const wallTime = (instant: Date, timezone: string): WallTime => {
    const parts = Object.fromEntries(
        formatterFor(timezone).formatToParts(instant)
            .filter((part) => part.type !== "literal")
            .map((part) => [part.type, Number(part.value)])
    );
    return {
        year: parts.year,
        month: parts.month,
        day: parts.day,
        hour: parts.hour,
        minute: parts.minute,
        second: parts.second,
        millisecond: instant.getUTCMilliseconds(),
    };
};

const wallStamp = (wall: WallTime) => Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
    wall.millisecond,
);

const sameWallTime = (left: WallTime, right: WallTime) =>
    left.year === right.year
    && left.month === right.month
    && left.day === right.day
    && left.hour === right.hour
    && left.minute === right.minute
    && left.second === right.second
    && left.millisecond === right.millisecond;

const zoneOffset = (instant: Date, timezone: string) => {
    const wall = wallTime(instant, timezone);
    return wallStamp(wall) - instant.getTime();
};

const resolveWallTime = (wall: WallTime, timezone: string): Date | null => {
    const naive = wallStamp(wall);
    const offsets = new Set(
        [-2 * DAY_MS, -DAY_MS, 0, DAY_MS, 2 * DAY_MS]
            .map((delta) => zoneOffset(new Date(naive + delta), timezone))
    );
    const matches = [...offsets]
        .map((offset) => new Date(naive - offset))
        .filter((candidate) => sameWallTime(wallTime(candidate, timezone), wall))
        .sort((left, right) => left.getTime() - right.getTime());
    // Nonexistent spring-forward times have no match. Ambiguous fall-back
    // times intentionally use the earlier instant, matching the backend.
    return matches[0] ?? null;
};

const shiftedWallTime = (
    base: WallTime,
    repeatUnit: ScheduledJob["repeatUnit"],
    repeatInterval: number,
    index: number,
): WallTime | null => {
    if (repeatUnit === "MONTH") {
        const monthIndex = base.month - 1 + repeatInterval * index;
        const year = base.year + Math.floor(monthIndex / 12);
        const month = ((monthIndex % 12) + 12) % 12;
        const probe = new Date(Date.UTC(year, month, base.day));
        // RFC 5545 recurrence skips invalid month days (Jan 31 -> Mar 31).
        if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month) return null;
        return { ...base, year, month: month + 1 };
    }
    const multiplier = repeatUnit === "MINUTE" ? 60_000
        : repeatUnit === "HOUR" ? 3_600_000
            : repeatUnit === "WEEK" ? 7 * DAY_MS
                : DAY_MS;
    const shifted = new Date(wallStamp(base) + repeatInterval * index * multiplier);
    return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
        hour: shifted.getUTCHours(),
        minute: shifted.getUTCMinutes(),
        second: shifted.getUTCSeconds(),
        millisecond: shifted.getUTCMilliseconds(),
    };
};

const initialIndex = (
    base: WallTime,
    rangeStart: WallTime,
    repeatUnit: ScheduledJob["repeatUnit"],
    repeatInterval: number,
) => {
    if (repeatUnit === "MONTH") {
        const months = (rangeStart.year - base.year) * 12 + rangeStart.month - base.month;
        return Math.max(0, Math.floor(months / repeatInterval) - 2);
    }
    const unit = repeatUnit === "MINUTE" ? 60_000
        : repeatUnit === "HOUR" ? 3_600_000
            : repeatUnit === "WEEK" ? 7 * DAY_MS
                : DAY_MS;
    return Math.max(0, Math.floor((wallStamp(rangeStart) - wallStamp(base)) / (unit * repeatInterval)) - 2);
};

/** Expand one schedule only for the visible range, using backend-equivalent wall-clock rules. */
export const occurrencesInRange = (
    job: ScheduledJob,
    rangeStart: Date,
    rangeEnd: Date,
): string[] => {
    const startsAt = new Date(job.startsAt);
    if (!Number.isFinite(startsAt.getTime())) return [];
    if (!job.repeat) {
        return startsAt >= rangeStart && startsAt < rangeEnd ? [startsAt.toISOString()] : [];
    }

    try {
        const base = wallTime(startsAt, job.timezone);
        const startWall = wallTime(rangeStart, job.timezone);
        const endWall = wallTime(rangeEnd, job.timezone);
        const untilWall = job.repeatUntil
            ? wallTime(new Date(job.repeatUntil), job.timezone)
            : null;
        const results: string[] = [];
        const firstIndex = initialIndex(base, startWall, job.repeatUnit, job.repeatInterval);
        const endStamp = wallStamp(endWall);
        for (let index = firstIndex; index < firstIndex + 10_000; index += 1) {
            const candidateWall = shiftedWallTime(base, job.repeatUnit, job.repeatInterval, index);
            if (!candidateWall) continue;
            const candidateStamp = wallStamp(candidateWall);
            if (untilWall && candidateStamp > wallStamp(untilWall)) break;
            if (candidateStamp > endStamp + 2 * DAY_MS) break;
            const occurrence = index === 0 ? startsAt : resolveWallTime(candidateWall, job.timezone);
            if (occurrence && occurrence >= rangeStart && occurrence < rangeEnd) {
                results.push(occurrence.toISOString());
            }
        }
        return results;
    } catch {
        // The API validates IANA timezones; this only protects rendering from
        // legacy/corrupt rows without taking down the entire schedule page.
        return [];
    }
};
